import {
  Content,
  Flex,
  fromTheme,
  Heading,
  IconButton,
  StrokeIcon,
} from '@acsbe/ui';
import { useState } from 'react';
import styled from 'styled-components';
import reviewPrompt from '../../assets/reviewPrompt.png';
import { useMerchantSession } from '../../context';
import { Sentiment, type UserFeedback } from '../../type/UserFeedback';
import type { ReviewState } from './type';

export const SentimentPrompt = ({
  setReview,
}: {
  setReview: (review: ReviewState) => void;
}) => {
  const [pending, setPending] = useState<ReviewState>('prompt');
  const { setUserFeedback, merchantDetails } = useMerchantSession();

  const onPositiveClick = () => {
    setPending('positive');
    setUserFeedback((prev: UserFeedback | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        sentiment: Sentiment.POSITIVE,
      };
    });

    setTimeout(() => {
      setReview('positive');
    }, 250);
  };

  const onNegativeClick = () => {
    setPending('negative');
    setUserFeedback((prev: UserFeedback | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        sentiment: Sentiment.NEGATIVE,
      };
    });

    setTimeout(() => {
      setReview('negative');
    }, 250);
  };

  return (
    <>
      <ReviewImage src={reviewPrompt} />
      <Flex
        align="center"
        scale="ghost"
        paddingBlockStart={10}
        paddingBlockEnd={8}
        paddingInline={8}
        gap={8}
      >
        <Flex align="center" gap={4} self="stretch">
          <Heading
            level={3}
            align="center"
            scale="base"
            emphasis="high"
            lineHeight={1.25}
          >
            Nice work!
          </Heading>
          <Content
            align="center"
            scale="alternative"
            emphasis="none"
            size="xl"
            lineHeight={1.5}
          >
            You acted on accessibility. Others are still catching up. How has
            your experience been so far?
          </Content>
        </Flex>
        <Flex row align="center" gap={8}>
          <StyledIconButton
            scale="base"
            className={pending === 'positive' ? 'active' : ''}
            padding={6}
            aria-label="Thumbs Up"
            data-mixpanel="thumbs-up"
            data-mixpanel-props={JSON.stringify({ sentiment: 'positive' })}
            radius="round"
            border={0.25}
            variant="element"
            onClick={onPositiveClick}
          >
            <StrokeIcon name="ThumbsUp" size={32} />
          </StyledIconButton>
          <StyledIconButton
            scale="base"
            className={pending === 'negative' ? 'active' : ''}
            padding={6}
            aria-label="Thumbs Down"
            data-mixpanel="thumbs-down"
            data-mixpanel-props={JSON.stringify({ sentiment: 'negative' })}
            radius="round"
            border={0.25}
            variant="element"
            onClick={onNegativeClick}
          >
            <StrokeIcon name="ThumbsDown" size={32} />
          </StyledIconButton>
        </Flex>
      </Flex>
    </>
  );
};

const ReviewImage = styled.img`
  max-width: 100%;
  height: 216px;
  object-fit: cover;
`;

const StyledIconButton = styled(IconButton)`
  --stroke: ${fromTheme('palette.scales.base.border.element')};

  &:hover {
    --fill: ${fromTheme('palette.scales.primary.background.elementHovered')};
    --text: ${fromTheme('palette.scales.primary.text.element')};
  }

  &:active {
    --fill: ${fromTheme('palette.scales.primary.background.elementHovered')};
    --text: ${fromTheme('palette.scales.primary.text.element')};
  }
`;
